import React, { useState, useEffect, useCallback } from 'react';

const Statistics = () => {
  const [counted, setCounted] = useState({
    apartments: 0,
    revenue: 0,
    clients: 0,
    training: 0
  });

  const stats = [
    { number: 17, key: "apartments" },
    { number: 1000000000, key: "revenue" },
    { number: 113, key: "clients" },
    { number: 4, key: "training" }
  ];

  const startCounting = useCallback(() => {
    let completed = 0;
    
    stats.forEach((stat) => {
      const duration = 1500; // Faster duration (1.5 seconds)
      const frameRate = 1000 / 60; 
      const totalFrames = Math.round(duration / frameRate);
      let frame = 0;

      const timer = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        
        // Use a simple ease-out quad for smoother noticeable speed
        const easeOut = 1 - (1 - progress) * (1 - progress);
        const currentNumber = stat.number * easeOut;

        if (frame >= totalFrames) {
          clearInterval(timer);
          setCounted(prev => ({ ...prev, [stat.key]: stat.number }));
          
          // Track when all 4 counters finish
          completed++;
          if (completed === stats.length) {
            // Wait 3 seconds, reset, and restart
            setTimeout(() => {
              setCounted({ apartments: 0, revenue: 0, clients: 0, training: 0 });
              startCounting();
            }, 3000);
          }
        } else {
          setCounted(prev => ({
            ...prev,
            [stat.key]: Math.floor(currentNumber)
          }));
        }
      }, frameRate);
    });
  }, []);

  useEffect(() => {
    startCounting();
    // Cleanup is handled by local intervals, but in a real app, 
    // you'd track these in a ref to clear on unmount.
  }, [startCounting]);

  const formatNumber = (num, key) => {
    if (key === 'revenue') {
      const val = (num / 1000000000).toFixed(1);
      return `${val}B+`;
    }
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K+`;
    return num.toLocaleString();
  };

  const ApartmentIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#f3b33d">
      <path d="M17 11V3H7v4H3v14h8v-4h2v4h8V11h-4zM7 19H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm4 4H9v-2h2v2zm0-4H9V9h2v2zm0-4H9V5h2v2zm4 8h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm4 12h-2v-2h2v2zm0-4h-2v-2h2v2z"/>
    </svg>
  );

  const MoneyIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#f3b33d">
      <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
    </svg>
  );

  const ClientIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#f3b33d">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
    </svg>
  );

  const TrainingIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#f3b33d">
      <path d="M12 3v10l3-3h6V3zm-2 12H5v5h5v-5zm2 5h5v-5h-5zM5 10h5V5H5zm12 5h5v-5h-5z"/>
    </svg>
  );

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@700;800&family=Comfortaa:wght@400&family=Cormorant+Garamond:wght@600&display=swap" rel="stylesheet" />
      
      <style>
        {`
          .statistics {
            background: #0a0f1c;
            padding: 60px 20px;
            color: #fff;
            display: flex;
            justify-content: center;
          }
          .stats-container { width: 100%; max-width: 1200px; }
          .section-header { text-align: center; margin-bottom: 40px; }
          .section-header h2 { font-family: 'Cormorant Garamond', serif !important; font-size: 2.8rem; margin: 10px 0; }
          .section-pre-title { font-family: 'Comfortaa', cursive !important; color: #f3b33d; letter-spacing: 2px; text-transform: uppercase; font-size: 0.8rem; }
          .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
          .stat-card { background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 15px; padding: 30px 15px; text-align: center; transition: all 0.3s ease; }
          .stat-card:hover { transform: translateY(-5px); border-color: #f3b33d; }
          .stat-icon { margin-bottom: 12px; }
          .stat-number {
            font-family: 'Inter', sans-serif !important;
            font-size: 1.6rem !important;
            font-weight: 800 !important;
            letter-spacing: -0.2px !important;
            font-variant-numeric: tabular-nums !important;
            margin-bottom: 6px;
            line-height: 1.2;
            display: block;
            background: linear-gradient(135deg, #f3b33d 0%, #f6d69b 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          .stat-label { font-family: 'Comfortaa', cursive !important; font-size: 0.8rem; color: #a0aec0; line-height: 1.4; max-width: 90%; margin: 0 auto; }
          @media (max-width: 992px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } }
          @media (max-width: 576px) { 
            .stats-grid { grid-template-columns: 1fr; }
            .section-header h2 { font-size: 2.2rem; }
            .stat-number { font-size: 1.4rem !important; }
          }
        `}
      </style>

      <section className="statistics">
        <div className="stats-container">
          <div className="section-header">
            <span className="section-pre-title">Our Achievements</span>
            <h2>Track <span style={{color: '#f3b33d'}}>Record</span></h2>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon"><ApartmentIcon /></div>
              <span className="stat-number">{formatNumber(counted.apartments, 'apartments')}</span>
              <div className="stat-label">Apartments Sold in One Week</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon"><MoneyIcon /></div>
              <span className="stat-number">{formatNumber(counted.revenue, 'revenue')}</span>
              <div className="stat-label">1+ Billion ETB Totally Closed</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon"><ClientIcon /></div>
              <span className="stat-number">{formatNumber(counted.clients, 'clients')}</span>
              <div className="stat-label">113 Successive Clients</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon"><TrainingIcon /></div>
              <span className="stat-number">{formatNumber(counted.training, 'training')}</span>
              <div className="stat-label">Major Real Estate Trainings</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Statistics;